import { nonNull } from 'utils'

// Method assumes children to be IDs
export const getChildren = (node, treemap, fieldGetter = (n) => n) => {
  return {
    ...fieldGetter(node),
    children: node.children.map((cId) => getChildren(treemap[cId], treemap, fieldGetter)),
  }
}

export const getParentsPathArray = (node, treemap, fieldGetter = (n) => n) => {
  if (!node) return []
  const { parentId } = node
  const parent = treemap[parentId]
  if (!parentId || !parent || parent.isRoot) return [fieldGetter(node)]
  return [...getParentsPathArray(parent, treemap, fieldGetter), fieldGetter(node)]
}

// Method assumes children to be entities
export const getTreeNodesAsArray = (roots) => {
  return roots.reduce((arr, r) => [...arr, r, ...getTreeNodesAsArray(r.children)], [])
}

/** ********************* TREE TRAVERSING FUNCTIONS ********************** */

export const traverseAndFindAnyLeafNode = (tree) => {
  if (tree.roots.length === 0) return null
  const getLeaf = (node) => {
    if (node.children.length === 0) return node
    return getLeaf(node.children[0])
  }
  return getLeaf(tree.roots[0])
}

export const traverseAndFind = (tree, searchId, getField = (n) => n.id) => {
  const root = tree.roots.find((r) => getField(r) === searchId)
  if (root) return root

  return tree.roots.map((r) => traverseAndFindNode(r, searchId, getField)).find(nonNull)
}

export const traverseAndApply = (tree, searchId, apply = (n) => n, getField = (n) => n.id) => {
  const isRoot = tree.roots.some((r) => getField(r) === searchId)
  if (isRoot) {
    tree.roots = tree.roots.map((r) => (getField(r) === searchId ? apply(r) : r))
  } else {
    const parent = traverseAndFindParent(tree, searchId, getField)
    const index = parent.children.findIndex((c) => getField(c) === searchId)
    parent.children[index] = apply(parent.children[index])
  }
  return tree
}

export const traverseAndFindParent = (tree, searchId, getField = (n) => n.id) => {
  const root = tree.roots.find((r) => getField(r) === searchId)
  if (root) return null
  return tree.roots.map((r) => traverseAndFindParentNode(r, searchId, getField)).find(nonNull)
}

export const traverseAndDelete = (tree, searchedNode) => {
  const isRoot = tree.roots.find((r) => r.id === searchedNode.id)
  if (isRoot) {
    tree.roots = tree.roots.filter((r) => r.id !== searchedNode.id)
  } else {
    tree.roots.forEach((r) => traverseAndDeleteNode(r, searchedNode))
  }
  return tree
}

export const traverseAndAppend = (tree, parentNode, newNode) => {
  if (!parentNode) {
    tree.roots = [...tree.roots, newNode]
  } else {
    const parent = traverseAndFind(tree, parentNode.id)
    parent.children = [...parent.children, newNode]
  }
  return tree
}

export const traverseAndReplace = (tree, oldNode, newNode) => {
  return traverseAndApply(tree, oldNode.id, () => newNode)
}

// Recursive helper functions
const traverseAndFindNode = (treeRoot, searchId, getField = (n) => n.id) => {
  if (getField(treeRoot) === searchId) return treeRoot
  if (!treeRoot.children) return null
  return (
    treeRoot.children.map((c) => traverseAndFindNode(c, searchId, getField)).find(nonNull) || null
  )
}

const traverseAndFindParentNode = (treeRoot, searchId, getField = (n) => n.id) => {
  if (!treeRoot.children) return null
  if (treeRoot.children.find((c) => getField(c) === searchId)) return treeRoot
  return (
    treeRoot.children.map((c) => traverseAndFindParentNode(c, searchId, getField)).find(nonNull) ||
    null
  )
}

// Recursive function to delete node in tree
const traverseAndDeleteNode = (treeRoot, searchedNode) => {
  if (!treeRoot.children) return
  // 1.) search for node in the tree
  if (treeRoot.children.some((child) => child.id === searchedNode.id)) {
    // 2.) Delete from children
    treeRoot.children = treeRoot.children.filter((child) => child.id !== searchedNode.id)
  } else treeRoot.children.forEach((child) => traverseAndDeleteNode(child, searchedNode))
}
